import React, { useState } from 'react';
import styled from 'styled-components';
import { Row, Col, Divider } from 'antd';
import { Trans, useTranslation } from 'next-i18next';
import Image from 'next/legacy/image';

import { CCPO_FEATURES } from '@source/pages/CarroCertified/constants';
import { IRegionProps } from '@source/interface';
import { RightOutlined } from '@ant-design/icons';
import CustomButton from '@source/components/CustomButton/CustomButton';
import CCPOFeaturesModal from '@source/pages/CarroCertified/components/CCPOFeaturesModal/CCPOFeaturesModal';
// import CCPOFeaturesModal from './CCPOFeaturesModal';

const StyledWrapper = styled.section`
  padding-bottom: 44px;
  @media screen and (min-width: 768px) {
    padding-bottom: 48px;
  }

  [ant-click-animating-without-extra-node='true']::after {
    display: none;
  }
`;

const StyledRow = styled(Row)`
  .content-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 420px;
    margin-top: 10px;

    .content-title {
      width: 100%;
      color: ${(props) => props.theme.color.primaryBase};
      font-size: 20px;
      ${(props) => props.theme.typo.familyGoogle.bold}
      margin-bottom: 12px;

      @media screen and (min-width: 768px) {
        font-size: 24px;
      }
    }

    .content-desc {
      font-size: 14px;
      color: ${(props) => props.theme.color.quartzColor};
      ${(props) => props.theme.typo.familyGoogle.regular}
      margin-bottom: 20px;
    }

    .link-text {
      width: 100%;
      cursor: pointer;
    }
  }

  @media screen and (min-width: 768px) {
    .content-wrapper {
      margin: 0px auto;
      padding: 0 20px;
    }
  }
`;

const StyledButton = styled(CustomButton)`
  font-size: 14px;
  width: auto;
  color: ${(props) => props.theme.color.primaryBase};
  ${(props) => props.theme.typo.familyGoogle.semiBold}
  padding: 0;
  background-color: transparent;
  border: none !important;
  height: auto;
  justify-content: flex-start;

  &:hover {
    color: ${(props) => props.theme.color.primaryBase};
    background-color: transparent;
  }

  &:focus {
    background-color: transparent;
    color: ${(props) => props.theme.color.primaryBase};
    box-shadow: none;
    outline: none;
  }

  .anticon {
    line-height: 0;
  }
`;

const StyledDivider = styled(Divider)`
  border-color: ${(props) => props.theme.color.primaryBase};
  margin: 24px 0;

  @media screen and (min-width: 576px) {
    margin: 45px 0;
  }
`;

type TFeatureModalProps = {
  modalTitle?: string;
  modalBody?: string;
};

type TFeatureLinkProps = {
  text: string;
};

type TCCPOFeatureProps = {
  key: string;
  title: string;
  description: string;
  image: string;
  modal?: TFeatureModalProps;
  link?: TFeatureLinkProps;
};

const CCPOPageFeatures = ({ region }: IRegionProps) => {
  const content = CCPO_FEATURES(region || 'sg') || {};
  const [modalContent, setModalContent] = useState<TFeatureModalProps | null>(null);
  const [isModalOpen, setModalOpen] = useState(false);

  const { t } = useTranslation('ccpo');
  const onHandleLinkClick = (modal: TFeatureModalProps) => {
    setModalContent(modal);
    setModalOpen(true);
  };

  const renderFeatureItem = ({ key, title, description, image, link, modal }: TCCPOFeatureProps, index: number) => (
    <StyledRow key={key} align="middle" role="article" aria-label={`Feature ${index + 1}`}>
      <Col sm={24} md={{ span: 12, order: index % 2 === 0 ? 1 : 2 }}>
        <figure role="figure" aria-label="Feature illustration">
          <Image src={image} width={538} height={334} alt={`Illustration for ${t(title)}`} aria-hidden="true" />
        </figure>
      </Col>
      <Col xs={24} md={{ span: 12, order: index % 2 === 0 ? 2 : 1 }}>
        <div className="content-wrapper">
          <h3 className="content-title">
            <Trans ns="ccpo" defaults={t(title || '')} components={{ br: <br /> }} />
          </h3>
          <p className="content-desc">{t(description)}</p>
          {link && modal && (
            <StyledButton
              className="link-text"
              variant="text"
              onClick={() => onHandleLinkClick(modal)}
              aria-label={`Learn more about ${t(title)}`}
            >
              {t(link?.text)} <RightOutlined style={{ fontSize: 16 }} aria-hidden="true" />
            </StyledButton>
          )}
        </div>
      </Col>
    </StyledRow>
  );

  const renderFeatures = () =>
    content.map((item: TCCPOFeatureProps, index: number) => (
      <React.Fragment key={item.key}>
        {index !== 0 && index !== content?.length && <StyledDivider />}
        {renderFeatureItem(item, index)}
      </React.Fragment>
    ));

  return (
    <StyledWrapper role="region" aria-label="Carro Certified Program Features">
      {renderFeatures()}
      <CCPOFeaturesModal
        title={<Trans ns="ccpo" defaults={t(modalContent?.modalTitle || '')} components={{ br: <br /> }} />}
        visible={isModalOpen}
        handleClose={() => setModalOpen(false)}
        aria-label="Feature Details Modal"
      >
        <Trans
          ns="ccpo"
          defaults={t(modalContent?.modalBody || '')}
          components={{
            section: <section />,
            div: <div />,
            ul: <ul />,
            li: <li />,
            specificFont: <span style={{ fontFamily: 'Poppins' }} />,
          }}
        />
      </CCPOFeaturesModal>
    </StyledWrapper>
  );
};

export default CCPOPageFeatures;
